import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IrisSelectFilesModalComponent } from '@iris/common/modules/dms/components/modals/select-files-modal/select-files-modal.component';
import { switchMap, take, tap } from 'rxjs/operators';
import { Observable, merge } from 'rxjs';
import { IrisModalI, IrisModal } from '../../../models/IrisModal';
import { IrisFoldersTreeModalComponent } from '@iris/common/modules/dms/components/modals/folders-tree-modal/folders-tree-modal.component';
import { IrisDMSFolderI, IrisDmsRecentFilesFolder } from '../models/IrisDMSFolder';
import { IrisDmsTagModalComponent } from '@iris/common/modules/dms/components/tags/dms-tag-modal/dms-tag-modal.component';
import { IrisUploadFilesModalComponent } from '@iris/common/modules/dms/components/modals/upload-files-modal/upload-files-modal.component';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsFolderEditModalComponent } from '@iris/common/modules/dms/components/modals/folder-edit-modal/folder-edit-modal.component';
import {
  ResetModalFilesState,
} from '@iris/common/modules/dms/sandbox/actions/files.actions';
import { IrisImportZipFolderModalComponent } from '@iris/common/modules/dms/components/modals/import-zip-folder-modal/import-zip-folder-modal.component';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { IrisPreviewFileService } from '@iris/common/modules/preview-file/services/preview-file.service';
import { IrisDmsFolderAddModalComponent } from '@iris/common/modules/dms/components/modals/folder-add-modal/folder-add-modal.component';
import { IrisDmsFileShareLinkModalComponent } from '@iris/common/modules/dms/components/modals/file-share-link-modal/file-share-link-modal.component';
import { IrisDataRepoFile } from '@iris/common/modules/files-common/models/IrisDataRepoFile';
import { IrisDmsFolderShareLinkModalComponent } from '@iris/common/modules/dms/components/modals/folder-share-link-modal/folder-share-link-modal.component';
import { IrisDmsFileDeleteConfirmModalComponent } from '@iris/common/modules/dms/components/modals/file-delete-confirm-modal/file-delete-confirm-modal.component';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { IrisDmsSelectTagsModalComponent } from '@iris/common/modules/dms/components/tags/dms-select-tags-modal/dms-select-tags-modal.component';
import { IrisDMSTagI } from '@iris/common/modules/dms/models/IrisDMSTag';
import { IrisDmsFileRenameModalComponent } from '@iris/common/modules/dms/components/modals/file-rename-modal/file-rename-modal.component';
import { IrisDmsFolderRenameModalComponent } from '@iris/common/modules/dms/components/modals/folder-rename-modal/folder-rename-modal.component';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisMoveEntitiesModalComponent } from '../components/modals/move-entities-modal/move-entities-modal.component';
import { IrisDmsFoldersStore } from '../store/folders.signal-store';
import { IrisDmsGlobalStore } from '../store/dms-global.signal-store';
import { IrisDmsContentStore } from '../store/dms-content.signal-store';
import { IrisOfficeEditorComponent } from '../components/modals/office-editor/office-editor.component';
import { IrisFilesService } from '../services/files.service';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { IrisSubjectI } from '@iris/common/models/IrisSubject';
import { IrisFilesModalExtendedComponent } from '../components/modals/files-modal-extended/files-modal-extended.component';
import { IrisModuleFolderService } from '@iris/common/services/docs/module-folder.service';
import { BulkFileUploader } from '../../file-uploader/bulk-file-uploader';

export interface IrisFoldersTreeModalParamsI {
  label: string;
  foldersToExclude?: string[];
  multiselect?: boolean;
  showSelectBtn?: boolean;
  showProjectSelector?: boolean;
  projectToExclude?: number;
  keepSelectedFolder?: boolean;
  foldersProjectId?: number;
}

export interface SelectFilesModalParamsI {
  selectedFiles?: IrisDMSFileI[];
  multiple?: boolean;
  projectId?: number;
  keepSelectedFolder?: boolean;
  showProjectSelector?: boolean;
  showTileView?: boolean;
  showFileUploader?: boolean;
}

export interface UploadFilesModalParamsI {
  fileId?: string;
  fileName?: string;
  uploader?: BulkFileUploader;
  folderId?: string;
  isUploadVersion?: boolean;
}

@Injectable({ providedIn: 'root' })
export class IrisDmsModalsSandbox {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly previewFileService = inject(IrisPreviewFileService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly filesService = inject(IrisFilesService);
  private readonly moduleFolderService = inject(IrisModuleFolderService);

  private readonly dmsFoldersStore = inject(IrisDmsFoldersStore);
  private readonly dmsGlobalStore = inject(IrisDmsGlobalStore);
  private readonly dmsContentStore = inject(IrisDmsContentStore);

  readonly uploadMinimizedModalShown = this.dmsGlobalStore.uploadMinimizedModalShown;

  public showSelectFilesModal(params: SelectFilesModalParamsI): IrisModalI {
    const {
      selectedFiles,
      keepSelectedFolder,
      showProjectSelector,
      showTileView,
      showFileUploader,
      multiple,
    } = params;

    this.foldersModalViewShown(null, keepSelectedFolder ?? true, true);
    const dialogRef: MatDialogRef<IrisSelectFilesModalComponent> = this.dialog
      .open(IrisSelectFilesModalComponent, {
        data: {
          selectedFiles: (selectedFiles ?? []).map(file => Object.assign(
            {
              fileSize: undefined,
              country: undefined,
              company: undefined,
              department: undefined,
              workflowState: undefined,
              attachedIn: undefined,
              searchHints: undefined,
              previewId: undefined,
              curUserId: undefined,
              parentIds: undefined,
              projectIds: undefined,
              contentMultifield: undefined,
            },
            file,
            {
              createdBy: +file.createdBy,
              contentLanguage: [file.contentLanguage],
            },
          )),
          multiple: multiple ?? true,
          showProjectSelector: showProjectSelector ?? false,
          showTileView: showTileView ?? false,
          showFileUploader: showFileUploader ?? true,
        },
        panelClass: 'modal-fullscreen',
      });

    const modal = IrisModal.fromDialogRef(dialogRef);
    this.foldersModalViewHidden(modal.onSuccess$, modal.onClose$).pipe(
      tap(() => this.store.dispatch(ResetModalFilesState())),
    ).subscribe();
    return modal;
  }

  public showMoveFolderModal(movingFolderId: string): IrisModalI {
    this.foldersModalViewShown([movingFolderId]);
    const dialogRef = this.dialog.open(IrisMoveEntitiesModalComponent);
    const modal = IrisModal.fromDialogRef(dialogRef);

    this.foldersModalViewHidden(modal.onSuccess$, modal.onClose$).subscribe();

    modal.onSuccess$.pipe(
      take(1),
      tap(({ targetFolderId, selectTargetFolder }) => this.moveFolder(movingFolderId, targetFolderId, false, false, selectTargetFolder)),
    ).subscribe();

    return modal;
  }

  public showCopyFolderModal(copyingFolderId: string, projectToExclude?: number): IrisModalI {
    const { onSuccess$, onClose$, dialogRef } = this.showFoldersTreeModal({
      label: 'label.Copy',
      foldersToExclude: [copyingFolderId],
      showProjectSelector: true,
      projectToExclude,
    });

    onSuccess$.pipe(
      take(1),
      tap((id: string) => this.copyFolder(copyingFolderId, id)),
    ).subscribe();

    return new IrisModal({
      onSuccess$,
      onClose$,
      dialogRef,
    });
  }

  public showCopyFoldersAndFilesModal(foldersToExclude: string[], selectedFolderId: string): IrisModalI {
    const { onSuccess$, onClose$, dialogRef } = this.showFoldersTreeModal({
      label: 'label.Copy',
      foldersToExclude,
      showProjectSelector: true,
    });

    onSuccess$.pipe(
      take(1),
      tap((id: string) => {
        this.copySelectedFoldersAndFiles(id, selectedFolderId);
      }),
    ).subscribe();

    return new IrisModal({
      onSuccess$,
      onClose$,
      dialogRef,
    });
  }

  public showMoveFilesModal(filesIds: string[], selectedFolderId: string): IrisModalI {
    this.foldersModalViewShown();
    const dialogRef = this.dialog.open(IrisMoveEntitiesModalComponent);
    const modal =  IrisModal.fromDialogRef(dialogRef);

    modal.onSuccess$.pipe(
      take(1),
      tap((result: { targetFolderId: string; selectTargetFolder: boolean }) => {
        this.moveSelectedFiles(filesIds, result.targetFolderId, selectedFolderId, result.selectTargetFolder);
      }),
    ).subscribe();

    this.foldersModalViewHidden(modal.onSuccess$, modal.onClose$).subscribe();

    return modal;
  }

  public showMoveFoldersAndFilesModal(selectedFolderId: string, undo: boolean, excludedFolders = []): IrisModalI {
    this.foldersModalViewShown(excludedFolders);
    const dialogRef = this.dialog.open(IrisMoveEntitiesModalComponent);
    const modal = IrisModal.fromDialogRef(dialogRef);

    this.foldersModalViewHidden(modal.onSuccess$, modal.onClose$).subscribe();

    modal.onSuccess$.pipe(
      take(1),
      tap(({ targetFolderId, selectTargetFolder }) => {
        this.moveSelectedFoldersAndFiles(targetFolderId, selectedFolderId, undo, selectTargetFolder);
      }),
    ).subscribe();

    return modal;
  }

  public showCopyFilesModal(files: string[], selectedFolderId: string): IrisModalI {
    const { onSuccess$, onClose$, dialogRef } = this.showFoldersTreeModal({
      label: 'label.Copy',
      keepSelectedFolder: true,
      showProjectSelector: true,
    });

    onSuccess$.pipe(
      take(1),
      tap((folderId: string) => this.copySelectedFiles(files, folderId, selectedFolderId)),
    ).subscribe();

    return new IrisModal({
      onSuccess$,
      onClose$,
      dialogRef,
    });
  }

  public showFoldersTreeModal(params: IrisFoldersTreeModalParamsI): IrisModalI<IrisFoldersTreeModalComponent, IrisDMSFolderI[] | string> {
    const {
      label,
      foldersToExclude = [],
      multiselect = false,
      showSelectBtn = true,
      showProjectSelector = false,
      keepSelectedFolder = false,
      projectToExclude,
      foldersProjectId,
    } = params;

    this.foldersModalViewShown(foldersToExclude, keepSelectedFolder);

    const dialogRef = this.dialog.open(IrisFoldersTreeModalComponent, {
      data: {
        label,
        multiselect,
        showSelectBtn,
        showProjectSelector,
        projectToExclude,
        foldersProjectId,
      },
    });

    const modal = IrisModal.fromDialogRef(dialogRef);
    this.foldersModalViewHidden(modal.onSuccess$, modal.onClose$).subscribe();

    return modal;
  }

  public showUploadFilesModal(folderId: string, uploader?: BulkFileUploader): IrisModalI {
    return this.openUploadFilesModal({ folderId, uploader });
  }

  public showUploadVersionModal(fileId: string, fileName: string, folderId?: string): IrisModalI {
    return this.openUploadFilesModal({ fileId, fileName, folderId, isUploadVersion: true });
  }

  private openUploadFilesModal(data: UploadFilesModalParamsI): IrisModalI {
    const dialogRef: MatDialogRef<IrisUploadFilesModalComponent> = this.dialog
      .open(IrisUploadFilesModalComponent, {
        data,
      });

    return IrisModal.fromDialogRef(dialogRef);
  }

  public showTagModal(tag?: IrisDMSTagI): IrisModalI {
    const dialogRef: MatDialogRef<IrisDmsTagModalComponent> = this.dialog
      .open(IrisDmsTagModalComponent, {
        data: { tag },
      });

    return IrisModal.fromDialogRef(dialogRef);
  }

  public showFolderRenameModal(folderId: string, folderName: string): IrisModalI {
    const dialogRef: MatDialogRef<IrisDmsFolderRenameModalComponent> = this.dialog
      .open(IrisDmsFolderRenameModalComponent, {
        data: { folderId, folderName },
      });
    return IrisModal.fromDialogRef(dialogRef);
  }

  public showDeleteFileModal(file: IrisDMSFileI, versionId: string): IrisModalI {
    const dialogRef: MatDialogRef<IrisDmsFileDeleteConfirmModalComponent> = this.dialog
      .open(IrisDmsFileDeleteConfirmModalComponent, {
        data: { file, versionId },
        maxWidth: '500px',
      });

    return IrisModal.fromDialogRef(dialogRef);
  }

  private foldersModalViewShown(
    foldersToExclude?: string[],
    keepSelectedFolder = false,
    resetPaginationOnFolderSelect = false,
  ): void {
    const rootFolder = this.dmsFoldersSandbox.currentProjectRootFolder();
    if (keepSelectedFolder) {
      const selectedFolderId = this.dmsFoldersSandbox.selectedFolderId();
      const currentProjectId = this.globalSandbox.currentProjectId();
      let folderId = '';
      if (selectedFolderId && selectedFolderId !== IrisDmsRecentFilesFolder.id) {
        folderId = selectedFolderId;
      } else {
        const shouldRequestRootFolder = !!currentProjectId && !rootFolder;
        if (shouldRequestRootFolder) {
          this.dmsFoldersSandbox.getProjectRootFolder(currentProjectId);
        } else if (rootFolder?.id) {
          folderId = rootFolder.id;
        }
      }
      this.dmsFoldersSandbox.setSelectedFolderModal(folderId, resetPaginationOnFolderSelect);
      this.dmsFoldersSandbox.changeProjectFolderModal(currentProjectId);
      if (folderId) {
        this.dmsFoldersSandbox.getParentFolders(folderId, true);
      }
    } else if(rootFolder) {
      this.dmsFoldersSandbox.expandFolder(rootFolder.id, rootFolder.isSystem);
    }

    this.dmsFoldersStore.modalViewShown(foldersToExclude);
  }

  private foldersModalViewHidden(onSuccess$: Observable<unknown>, onClose$: Observable<unknown>): Observable<unknown> {
    return merge(onSuccess$, onClose$)
      .pipe(
        take(1),
        tap(() => {
          this.dmsFoldersStore.modalViewHidden();
          this.dmsGlobalStore.setSelectedModalProjectId(null);
        }),
      );
  }

  moveFolder(folderId: string, targetFolderId: string, silence = false, undo = false, selectTargetFolder = false): void {
    this.dmsFoldersStore.moveFolder$({ folderId, targetFolderId, undo, silence, selectTargetFolder });
  }

  copyFolder(folderId: string, targetFolderId: string): void {
    const folder = this.dmsFoldersSandbox.getFolderById(folderId)();
    const sourceFolderId = folder.parentId;
    this.dmsFoldersStore.copyFolder$({ folderId, targetFolderId, sourceFolderId });
  }

  copySelectedFoldersAndFiles(targetFolderId: string, selectedFolderId: string): void {
    this.dmsContentStore.copySelectedFoldersAndFiles$({ targetFolderId, selectedFolderId });
  }

  moveSelectedFiles(entitiesIds: string[], targetFolderId: string, selectedFolderId: string, selectTargetFolder = false): void {
    this.dmsContentStore.moveContentEntities$({ entitiesIds, targetFolderId, selectedFolderId, selectTargetFolder, showSuccessMessage: true });
  }

  moveSelectedFoldersAndFiles(targetFolderId: string, selectedFolderId: string, undo: boolean, selectTargetFolder = false): void {
    this.dmsContentStore.moveSelectedFoldersAndFiles$({ targetFolderId, selectedFolderId, undo, selectTargetFolder });
  }

  moveContentEntities(entitiesIds: string[], targetFolderId: string, selectedFolderId: string): void {
    this.dmsContentStore.moveContentEntities$({ entitiesIds, targetFolderId, selectedFolderId, selectTargetFolder: true, showSuccessMessage: false });
  }

  copySelectedFiles(filesIds: string[], targetFolderId: string, selectedFolderId: string): void {
    this.dmsContentStore.copySelectedFiles$({ filesIds, targetFolderId, selectedFolderId });
  }

  showUploadMinimizedModal(showUploadMinimizedModal: boolean): void {
    this.dmsGlobalStore.showUploadMinimizedModal(showUploadMinimizedModal);
  }

  showEditFolderModal(folder: IrisDMSFolderI): void {
    this.dialog.open(IrisDmsFolderEditModalComponent, {
      data: { folder },
      maxWidth: '500px',
    });
  }

  showAddFolderModal(parentFolderId: string = null, setSelected = false): void {
    this.dialog.open(IrisDmsFolderAddModalComponent, {
      data: { parentFolderId, setSelected },
      maxWidth: '500px',
    });
  }

  public showImportZipFolderModal(folderId: string): IrisModalI {
    const dialogRef: MatDialogRef<IrisImportZipFolderModalComponent> = this.dialog
      .open(IrisImportZipFolderModalComponent, {
        data: { folderId },
        maxWidth: '500px',
      });

    return IrisModal.fromDialogRef(dialogRef);
  }

  showFilePreviewModal(
    file: IrisDMSFileI | IrisDataRepoFile,
    versionId?: string,
    openDms?: boolean,
    showFolderButton = true,
    showFileInfo = true,
  ): void {
    this.previewFileService.openPreviewFileModal({
      fileInfo: file,
      filesList: this.dmsFilesSandbox.filesForPreivew(),
      versionId,
      openDms,
      showGoToFolder: showFolderButton,
      showFileInfo,
      saveRecentFile: true,
    });
  }

  public showFileShareLinkModal(file: IrisDMSFileI): void {
    this.dialog.open(IrisDmsFileShareLinkModalComponent, {
      data: { file },
      maxWidth: '500px',
    });
  }

  public showFolderShareLinkModal(folder: IrisDMSFolderI): void {
    this.dialog.open(IrisDmsFolderShareLinkModalComponent, {
      data: { folder },
      maxWidth: '500px',
    });
  }

  public showSelectTagsModal(selectedTags: IrisDMSTagI[] = []): IrisModalI {
    const dialogRef: MatDialogRef<IrisDmsSelectTagsModalComponent> = this.dialog
      .open(IrisDmsSelectTagsModalComponent, {
        height: '100vh',
        data: { selectedTags },
      });

    return IrisModal.fromDialogRef(dialogRef);
  }

  public showFileRenameModal(file: IrisDMSFileI, fromForm = false, isFileAttached = false): IrisModalI {
    const dialogRef: MatDialogRef<IrisDmsFileRenameModalComponent> = this.dialog
      .open(IrisDmsFileRenameModalComponent, {
        minHeight: fromForm ? 250 : undefined,
        data: {
          fileName: file.name,
          folderId: file.parentId,
          fromForm,
          isFileAttached,
        },
      });

    return IrisModal.fromDialogRef(dialogRef);
  }

  public showOfficeEditorModal(file: IrisDMSFileI): void {
    if (!this.filesService.isOfficeEditableDocument(file?.mimeType) && !this.filesService.isOfficeEditableExtension(file?.name)) {
      return;
    }
    this.dialog
      .open(IrisOfficeEditorComponent, {
        data: { file },
        panelClass: 'modal-fullscreen',
      });
  }

  openModuleFilesModalExtended(module: IrisModules, subjects: IrisSubjectI[] = [], accept: string): Promise<IrisDMSFileI> {
    return new Promise(resolve => {
      this.moduleFolderService.getModuleFiles(module, subjects).pipe(
        take(1),
        switchMap((module_files: IrisDMSFileI[]) => {
          const filesExtendedModalRef = this.dialog.open(IrisFilesModalExtendedComponent, {
            data: {
              accept,
              subjects,
              module,
              module_files,
            },
          });

          return filesExtendedModalRef.componentInstance.result$.pipe(take(1));
        }),
        tap((files: IrisDMSFileI[]) => resolve(files?.[0])),
      ).subscribe();
    });
  }

  /**
   * Opens the files upload modal restricted to a single target folder:
   * no files list and no select button, only the uploader and its progress.
   * Resolves with the files uploaded during the modal session.
   */
  public showUploadFilesToFolderModal(folderId: string, accept?: string): IrisModalI<IrisFilesModalExtendedComponent, IrisDMSFileI[]> {
    const dialogRef: MatDialogRef<IrisFilesModalExtendedComponent, IrisDMSFileI[]> = this.dialog
      .open(IrisFilesModalExtendedComponent, {
        data: {
          folderId,
          accept,
          isUploadOnly: true,
        },
      });

    return IrisModal.fromDialogRef(dialogRef);
  }
}
